import { useState, useEffect } from "react";
import { authHeaders } from "../lib/auth";
import AppHeader from "./AppHeader";

// ── Colors (matches ProfileScreen / hydration screens) ────────────────
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D",
  green:"#1E8A4C", warning:"#F29423", danger:"#D93025",
};

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token).

const OZ_PER_L = 33.814;

// Browser timezone, read once. Free, no permission, no API. Sent on every log
// so the server resets intake at this user's local midnight.
const TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch(e) { return ''; } })();
// Local calendar date in the user's own timezone (the browser is already in it).
const todayLocal = () => new Date().toLocaleDateString('en-CA');

const SOURCES = [
  { key:"hub",      api:"WTR HUB",      label:"Generosity\u2122 Hub Water", short:"Hub water",    hint:"Pure RO \u00B7 Alkaline \u00B7 best absorption", hero:true,
    sizes:[{oz:8,v:"Glass"},{oz:12,v:"Cup"},{oz:16,v:"Bottle"},{oz:24,v:"Large"}] },
  { key:"filtered", api:"Filtered",     label:"Filtered Water",  short:"Filtered",     hint:"Standard filtration",
    sizes:[{oz:8,v:"Glass"},{oz:12,v:"Cup"},{oz:16,v:"Bottle"}] },
  { key:"tap",      api:"Tap",          label:"Tap Water",       short:"Tap water",    hint:"Unfiltered",
    sizes:[{oz:8,v:"Glass"},{oz:12,v:"Cup"},{oz:16,v:"Bottle"}] },
  { key:"coffee",   api:"Coffee / Tea", label:"Coffee or Tea",   short:"Coffee/Tea",   hint:"Counts with a mild diuretic offset",
    sizes:[{oz:8,v:"Small"},{oz:12,v:"Medium"},{oz:16,v:"Large"}] },
  { key:"sports",   api:"Sports Drink", label:"Sports Drink",    short:"Sports drink", hint:"With electrolytes",
    sizes:[{oz:12,v:"Bottle"},{oz:20,v:"Large"},{oz:32,v:"XL"}] },
  { key:"other",    api:"Other",        label:"Other",           short:"Other",        hint:"Juice, soda, and the rest",
    sizes:[{oz:8,v:"Small"},{oz:12,v:"Medium"},{oz:16,v:"Large"}] },
];

function Icon({ name, size=20, color=C.blue }) {
  const s=size, ic=color;
  const icons = {
    drop: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3C12 3 6 10 6 14a6 6 0 0 0 12 0c0-4-6-11-6-11Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    cup: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 8h11v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V8Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/><path d="M16 9h2a2 2 0 0 1 0 4h-2" stroke={ic} strokeWidth="1.5"/></svg>,
    bolt: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    dots: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="6" cy="12" r="1.6" fill={ic}/><circle cx="12" cy="12" r="1.6" fill={ic}/><circle cx="18" cy="12" r="1.6" fill={ic}/></svg>,
    star: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3l2.5 5.5L20 9.3l-4 4 1 6-5-2.8L7 19.3l1-6-4-4 5.5-.8L12 3Z" stroke={ic} strokeWidth="1.4" strokeLinejoin="round"/></svg>,
    undo: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M9 7L4 12l5 5" stroke={ic} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 12h11a5 5 0 0 1 0 10h-3" stroke={ic} strokeWidth="1.6" strokeLinecap="round"/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icons[name]||icons.drop}</span>;
}

const ICON_FOR = { hub:"drop", filtered:"drop", tap:"drop", coffee:"cup", sports:"bolt", other:"dots" };

export default function MyIntake({ onClose }) {
  const [email] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem('wtr_profile') || '{}'); return (p.email || '').trim().toLowerCase(); }
    catch(e) { return ''; }
  });

  const [intake, setIntake] = useState({ hub:"0", filtered:"0", tap:"0", coffee:"0", sports:"0", other:"0" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);       // `${key}-${oz}` or 'undo' while posting
  const [flash, setFlash] = useState(null);     // { text, tone }
  const [undoStack, setUndoStack] = useState([]); // session adds: [{ key, oz }]

  const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Load today's logged intake. If the stored intake_date is not today (in this
  // user's timezone), the server treats it as zero, so we show zero too.
  useEffect(() => {
    if (!validEmail) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wtr/profile?email=${encodeURIComponent(email)}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (!cancelled && data.ok && data.profile) {
          const fresh = data.profile.intake_date === todayLocal();
          if (fresh && data.profile.intake) {
            const ij = typeof data.profile.intake === 'string' ? JSON.parse(data.profile.intake) : data.profile.intake;
            setIntake(prev => ({ ...prev, ...ij }));
          }
          // stale or empty: leave the zeros already in state
        }
      } catch(e) { /* leave zeros */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [email, validEmail]);

  const ozFor = (key) => Math.round((parseFloat(intake[key]) || 0) * OZ_PER_L);
  const totalOz = SOURCES.reduce((s, src) => s + ozFor(src.key), 0);
  const totalL = (totalOz / OZ_PER_L).toFixed(1);

  const showFlash = (text, tone='good') => { setFlash({ text, tone }); setTimeout(() => setFlash(null), 1800); };

  const post = async (apiSource, amountOz) => {
    const res = await fetch(`${API_BASE}/api/wtr/hydration/intake`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ email, source: apiSource, amount: amountOz, unit: 'oz', tz: TZ }),
    });
    return res.json().then(data => ({ ok: res.ok, data }));
  };

  const logDrink = async (src, oz) => {
    if (!validEmail || busy) return;
    setBusy(`${src.key}-${oz}`);
    try {
      const { ok, data } = await post(src.api, oz);
      if (ok && data.ok && data.intake) {
        setIntake(prev => ({ ...prev, ...data.intake }));
        setUndoStack(s => [...s, { key: src.key, oz }]);
        showFlash(`Added ${oz} oz \u00B7 ${src.short}`, 'good');
      } else if (data.error === 'profile not found') {
        showFlash('Set up your Profile first', 'warn');
      } else {
        showFlash('Could not log that, try again', 'warn');
      }
    } catch(e) {
      showFlash('Network error, try again', 'warn');
    }
    setBusy(null);
  };

  const undoLast = async () => {
    if (!undoStack.length || busy) return;
    const last = undoStack[undoStack.length - 1];
    const src = SOURCES.find(s => s.key === last.key);
    if (!src) return;
    setBusy('undo');
    try {
      const { ok, data } = await post(src.api, -last.oz);
      if (ok && data.ok && data.intake) {
        setIntake(prev => ({ ...prev, ...data.intake }));
        setUndoStack(s => s.slice(0, -1));
        showFlash(`Removed ${last.oz} oz \u00B7 ${src.short}`, 'good');
      } else {
        showFlash('Could not undo, try again', 'warn');
      }
    } catch(e) {
      showFlash('Network error, try again', 'warn');
    }
    setBusy(null);
  };

  if (loading) {
    return (
      <div style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:40,height:40,border:`3px solid ${C.border}`,borderTopColor:C.blue,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
          <div style={{fontSize:14,color:C.muted,fontWeight:600}}>Loading your intake...</div>
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="my-intake-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}`}</style>

      <AppHeader
        variant="modal"
        title="My Intake"
        subtitle="Absorption, not just intake"
        onClose={onClose}
      />

      {/* Flash */}
      {flash && (
        <div style={{position:"sticky",top:65,zIndex:90,padding:"0 20px",animation:"fadeIn 0.2s ease"}}>
          <div style={{marginTop:10,padding:"10px 14px",borderRadius:12,fontSize:13,fontWeight:700,textAlign:"center",
            background: flash.tone==='good' ? `${C.green}12` : `${C.warning}12`,
            color: flash.tone==='good' ? C.green : C.warning,
            border:`1px solid ${flash.tone==='good' ? C.green : C.warning}30`}}>
            {flash.text}
          </div>
        </div>
      )}

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:18,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        {!validEmail && (
          <div style={{padding:"16px 18px",background:`${C.warning}10`,border:`1.5px solid ${C.warning}30`,borderRadius:16,fontSize:13,fontWeight:600,color:C.body}}>
            Add your email in Profile first. Your intake logs to your profile, so we need to know whose intake this is.
          </div>
        )}

        {/* Today total */}
        <div data-testid="intake-total" style={{background:`linear-gradient(160deg,${C.blueLight} 0%,${C.white} 100%)`,borderRadius:24,padding:"22px 20px",border:`1px solid ${C.border}`,textAlign:"center"}}>
          <div style={{fontSize:10,fontWeight:700,color:C.blue,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>Today</div>
          <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",gap:8}}>
            <span style={{fontSize:48,fontWeight:900,color:C.body,fontFamily:"Nunito,sans-serif",letterSpacing:-2,lineHeight:1}}>{totalOz}</span>
            <span style={{fontSize:18,fontWeight:800,color:C.muted}}>oz</span>
          </div>
          <div style={{fontSize:12,color:C.muted,marginTop:4}}>{totalL} L reference {"\u00B7"} feeds your Hydration Score</div>
          {undoStack.length > 0 && (
            <button onClick={undoLast} disabled={!!busy} data-testid="intake-undo"
              style={{marginTop:14,display:"inline-flex",alignItems:"center",gap:6,padding:"7px 16px",borderRadius:20,
                border:`1.5px solid ${C.border}`,background:C.white,color:C.blueDark,fontSize:12,fontWeight:800,
                cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontFamily:"inherit"}}>
              <Icon name="undo" size={14} color={C.blueDark}/> Undo last entry
            </button>
          )}
        </div>

        <div style={{fontSize:12,color:C.muted,marginTop:-6,marginBottom:-2,paddingLeft:2}}>Tap a size each time you drink. Resets each morning in your time zone.</div>

        {/* Source cards */}
        {SOURCES.map((src) => {
          const oz = ozFor(src.key);
          return (
            <div key={src.key} data-testid={`intake-source-${src.key}`} style={{
              background: src.hero ? C.blueLight : C.white,
              borderRadius:18, border:`1.5px solid ${src.hero ? `${C.blue}40` : C.border}`,
              padding:"16px", boxShadow: src.hero ? `0 2px 14px ${C.blue}14` : "none",
            }}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <div style={{width:38,height:38,borderRadius:10,flexShrink:0,background: src.hero ? C.blue : C.bg,display:"flex",alignItems:"center",justifyContent:"center",border: src.hero ? "none" : `1px solid ${C.border}`}}>
                  <Icon name={ICON_FOR[src.key]} size={20} color={src.hero ? "#fff" : C.blueDark}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <span style={{fontSize:15,fontWeight:800,color:C.body}}>{src.label}</span>
                    {src.hero && <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:9,fontWeight:800,padding:"2px 7px",borderRadius:10,background:C.green,color:"#fff",letterSpacing:0.6}}><Icon name="star" size={10} color="#fff"/>BEST</span>}
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:1}}>{src.hint}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:20,fontWeight:900,color: oz>0 ? C.blueDark : C.muted,fontFamily:"Nunito,sans-serif",lineHeight:1}}>{oz}</div>
                  <div style={{fontSize:10,fontWeight:700,color:C.muted}}>oz today</div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {src.sizes.map((sz) => {
                  const isBusy = busy === `${src.key}-${sz.oz}`;
                  return (
                    <button key={sz.oz} onClick={() => logDrink(src, sz.oz)} disabled={!validEmail || !!busy}
                      data-testid={`intake-add-${src.key}-${sz.oz}`}
                      style={{
                        flex:"1 1 0", minWidth:64, padding:"10px 6px", borderRadius:14,
                        border:`1.5px solid ${src.hero ? `${C.blue}45` : C.border}`,
                        background: isBusy ? C.bg : C.white,
                        cursor: (!validEmail || busy) ? "default" : "pointer",
                        opacity: (!validEmail || (busy && !isBusy)) ? 0.5 : 1,
                        display:"flex", flexDirection:"column", alignItems:"center", gap:1,
                        transition:"all 0.15s",
                      }}>
                      <span style={{fontSize:17,fontWeight:900,color:C.blueDark,fontFamily:"Nunito,sans-serif",lineHeight:1.1}}>{isBusy ? "\u2026" : `+${sz.oz}`}</span>
                      <span style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.4}}>{sz.v}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Wellness disclaimer */}
        <div style={{fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.5,padding:"4px 8px"}}>
          Generosity{"\u2122"} provides general wellness estimates to support healthy hydration habits. It is not medical advice.
        </div>

      </div>
    </div>
  );
}
